// AddBuilding.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { Search, MoreVertical, Home, ArrowLeft, Files } from 'lucide-react';
import BottomNav from "../../components/BottomNav.tsx";
import { baseurly } from '../../utils/apis.ts';
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";

interface BuildingData {
  id: number;
  title: string;
}

interface BuildingProps {
  building: BuildingData;
  onTitleChange: (id: number, title: string) => void;
}

// Building Component: Inline editing for a building’s title.
const Building: React.FC<BuildingProps> = ({ building, onTitleChange }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onTitleChange(building.id, e.target.value);
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setIsEditing(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto mb-4">

      <div className="bg-gray-100 p-4 rounded-lg">
        {isEditing ? (
          <input
            value={building.title}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            placeholder="Enter building title"
            className="w-full px-2 py-1 rounded border border-gray-300 focus:outline-none focus:border-blue-500"
          />
        ) : (
          <div className="text-gray-900 font-medium" onClick={handleTitleClick}>
            {building.title || "Enter building title"}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component: Manages building list and posts them to the API.
const AddBuilding: React.FC = () => {
  const [buildings, setBuildings] = useState<BuildingData[]>([]);
  const navigate = useNavigate();

  // Add a new building entry.
  const handleAddBuilding = () => {
    setBuildings([...buildings, { id: Date.now(), title: "" }]);
  };

  // Update the title for a specific building.
  const handleTitleChange = (id: number, title: string) => {
    setBuildings(prevBuildings =>
      prevBuildings.map(b => (b.id === id ? { ...b, title } : b))
    );
  };

  // Post each building to the API and then navigate to /view-buildings.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Filter out buildings with empty titles.
    const validBuildings = buildings.filter(b => b.title.trim() !== "");

    try {
      for (const building of validBuildings) {
        const response = await fetch(`${baseurly}api/building`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ building_name: building.title }),
        });

        if (!response.ok) {
          throw new Error(`Error posting building "${building.title}": ${response.statusText}`);
        }
      }
      console.log("All buildings posted successfully.");
      // After posting, navigate to the view page.
      navigate("/view-buildings");
    } catch (error) {
      console.error("Error posting buildings:", error);
    }
  };

  return (
    <div className="min-h-screen bg-blue-900 pb-20 flex flex-col ">
      {/* Header */}
      <div className="p-4 "><img src={digitalOceanImageUrl("new%20logos/Asset%204.png")} alt="KMC Logo" className="w-16 h-auto ml-auto h-24 w-27 " />  </div>
      
      {/* Search Bar */}
      <div className="px-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search for an instruction document..."
            className="w-full py-3 px-4 pr-10 rounded-full"
          />
          <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>
      <div className="p-4 flex justify-between items-center w-full max-w-5xl">
        <h1 className="text-white text-xl font-bold">Create Workshops</h1>
      </div>

      {/* Main Content */}
      <div className="bg-white p-6 rounded-t-3xl flex-1">
        <div className=" mb-6 bg-white rounded-full p-6">
          <div className="bg-white rounded-xl border p-8 flex justify-between items-center">
                <div>
                  <h2 className="text-blue-900 text-xl font-bold mb-2">Add Workshops</h2>
                  <p className="text-gray-600 text-sm max-w-xs">
                If there are no workshops added yet, please add a Workshop. Fill in the "Enter Workshop title" 
                and keep adding until finished. Then Click "Save Workshop" to save the Workshops.
                  </p>
                </div>
              <img src={digitalOceanImageUrl("assets/images/machineimage.jpg")} alt="12M EVS" className="w-27 h-24" />
            </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-blue-900 text-3xl font-bold mb-4">Workshops</h2>

            <button
              type="button"
              onClick={handleAddBuilding}
              className="bg-blue-900 text-white px-4 py-2 rounded-full hover:bg-blue-800 mb-4"
            >
              Add Workshop
            </button>

            {buildings.map(building => (
              <Building
                key={building.id}
                building={building}
                onTitleChange={handleTitleChange}
              />
            ))}

            <button
              type="submit"
              className="bg-blue-900 text-white p-3 rounded-full hover:bg-blue-800 mt-6"
            >
              Save Workshops
            </button>
          </div>
        </form>
      </div>
      <BottomNav showBack = {true} />
    </div>
  );
};

export default AddBuilding;
